import { combineReducers } from '@reduxjs/toolkit';

import { apiReducer, initialApiState } from 'utils/reduxUtils';

import { userSignout } from 'dux/auth/actions';

import {
  BUFFER_ANSWER,
  CLEAR_ANSWER_BUFFER,
  CONSULTATION_AGGRESSORS_FAILURE,
  CONSULTATION_AGGRESSORS_REQUEST,
  CONSULTATION_AGGRESSORS_SUCCESS,
  CONSULTATION_FRAGRANCES_FAILURE,
  CONSULTATION_FRAGRANCES_REQUEST,
  CONSULTATION_FRAGRANCES_SUCCESS,
  CONSULTATION_INGREDIENTS_FAILURE,
  CONSULTATION_INGREDIENTS_REQUEST,
  CONSULTATION_INGREDIENTS_SUCCESS,
  CONSULTATION_SCORING_FAILURE,
  CONSULTATION_SCORING_REQUEST,
  CONSULTATION_SCORING_SUCCESS,
  CONSULTATION_SKINCARE_FRAGRANCES_FAILURE,
  CONSULTATION_SKINCARE_FRAGRANCES_REQUEST,
  CONSULTATION_SKINCARE_FRAGRANCES_SUCCESS,
  CONSULTATION_SKINCARE_TEXTURES_FAILURE,
  CONSULTATION_SKINCARE_TEXTURES_REQUEST,
  CONSULTATION_SKINCARE_TEXTURES_SUCCESS,
  FEEDBACK_FRAGRANCES_FAILURE,
  FEEDBACK_FRAGRANCES_REQUEST,
  FEEDBACK_FRAGRANCES_SUCCESS,
  GET_HAIR_PROFILE_ERROR,
  GET_HAIR_PROFILE_PRODUCT_VARIANT_FAILURE,
  GET_HAIR_PROFILE_PRODUCT_VARIANT_REQUEST,
  GET_HAIR_PROFILE_PRODUCT_VARIANT_SUCCESS,
  GET_HAIR_PROFILE_REQUEST,
  GET_HAIR_PROFILE_SUCCESS,
  HAIRCARE_CONSULTATION_INCIS_FAILURE,
  HAIRCARE_CONSULTATION_INCIS_REQUEST,
  HAIRCARE_CONSULTATION_INCIS_SUCCESS,
  INITIALIZE_CONSULTATION_ERROR,
  INITIALIZE_CONSULTATION_REQUEST,
  INITIALIZE_CONSULTATION_SUCCESS,
  INVALIDATE_CONSULTATION,
  IS_EMAIL_CAPTURE_EXIT_CONSULTATION_MODAL_OPEN,
  RECOMMENDATION_FAILURE,
  RECOMMENDATION_REQUEST,
  RECOMMENDATION_SUCCESS,
  SAVE_ANSWER_ERROR,
  SAVE_ANSWER_REQUEST,
  SAVE_ANSWER_SUCCESS,
  SKINCARE_CONSULTATION_INCIS_FAILURE,
  SKINCARE_CONSULTATION_INCIS_REQUEST,
  SKINCARE_CONSULTATION_INCIS_SUCCESS,
  SKINCARE_CONSULTATION_INGREDIENTS_FAILURE,
  SKINCARE_CONSULTATION_INGREDIENTS_REQUEST,
  SKINCARE_CONSULTATION_INGREDIENTS_SUCCESS,
  SKINCARE_CONSULTATION_SCORING_FAILURE,
  SKINCARE_CONSULTATION_SCORING_REQUEST,
  SKINCARE_CONSULTATION_SCORING_SUCCESS,
} from './actionTypes';

const initialAnwersState = {
  data: null,
  didInvalidate: false,
  isSaving: false,
  isLoading: false,
  error: null,
  emailCaptureExitConsultationModalOpened: false,
};

const answers = (state = initialAnwersState, action) => {
  switch (action.type) {
    case userSignout.type:
      return initialAnwersState;
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
      return {
        ...initialAnwersState,
        emailCaptureExitConsultationModalOpened: state.emailCaptureExitConsultationModalOpened,
        isLoading: true,
      };
    case INITIALIZE_CONSULTATION_SUCCESS:
    case GET_HAIR_PROFILE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        data: action.answers,
      };
    case INVALIDATE_CONSULTATION:
      return {
        ...state,
        didInvalidate: true,
      };
    case SAVE_ANSWER_REQUEST:
      return {
        ...state,
        isSaving: true,
        error: null,
      };
    case SAVE_ANSWER_SUCCESS:
      return {
        ...state,
        isSaving: false,
        error: null,
        data: {
          ...state.data,
          ...action.answers,
        },
      };
    case INITIALIZE_CONSULTATION_ERROR:
    case SAVE_ANSWER_ERROR:
    case GET_HAIR_PROFILE_ERROR:
      return {
        ...state,
        isSaving: false,
        isLoading: false,
        error: action.error,
      };
    case IS_EMAIL_CAPTURE_EXIT_CONSULTATION_MODAL_OPEN:
      return { ...state, emailCaptureExitConsultationModalOpened: action.isOpen };
    default:
      return state;
  }
};

const bufferedAnswers = (state = {}, action) => {
  switch (action.type) {
    case userSignout.type:
      return {};
    case BUFFER_ANSWER:
      return {
        ...state,
        bufferedData: {
          ...(state.bufferedData ?? {}),
          ...action.answers,
        },
      };
    case CLEAR_ANSWER_BUFFER:
      return {
        ...state,
        bufferedData: null,
      };
    default:
      return state;
  }
};

const aggressors = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [
          CONSULTATION_AGGRESSORS_REQUEST,
          CONSULTATION_AGGRESSORS_SUCCESS,
          CONSULTATION_AGGRESSORS_FAILURE,
        ],
        state,
        action
      );
  }
};

const fragrances = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
      return initialApiState;
    case SAVE_ANSWER_SUCCESS:
      return action.answers.pref_fragrance || action.answers.pref_fragrance_free
        ? state
        : initialApiState;
    default:
      return apiReducer(
        [
          CONSULTATION_FRAGRANCES_REQUEST,
          CONSULTATION_FRAGRANCES_SUCCESS,
          CONSULTATION_FRAGRANCES_FAILURE,
        ],
        state,
        action
      );
  }
};

const skincareFragrances = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
      return initialApiState;
    default:
      return apiReducer(
        [
          CONSULTATION_SKINCARE_FRAGRANCES_REQUEST,
          CONSULTATION_SKINCARE_FRAGRANCES_SUCCESS,
          CONSULTATION_SKINCARE_FRAGRANCES_FAILURE,
        ],
        state,
        action
      );
  }
};

const skincareTextures = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
      return initialApiState;
    default:
      return apiReducer(
        [
          CONSULTATION_SKINCARE_TEXTURES_REQUEST,
          CONSULTATION_SKINCARE_TEXTURES_SUCCESS,
          CONSULTATION_SKINCARE_TEXTURES_FAILURE,
        ],
        state,
        action
      );
  }
};

const fragrancesFeedback = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
      return initialApiState;
    case SAVE_ANSWER_SUCCESS:
      return action.answers.pref_fragrance || action.answers.pref_fragrance_free
        ? state
        : initialApiState;
    default:
      return apiReducer(
        [FEEDBACK_FRAGRANCES_REQUEST, FEEDBACK_FRAGRANCES_SUCCESS, FEEDBACK_FRAGRANCES_FAILURE],
        state,
        action
      );
  }
};

const ingredients = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [
          CONSULTATION_INGREDIENTS_REQUEST,
          CONSULTATION_INGREDIENTS_SUCCESS,
          CONSULTATION_INGREDIENTS_FAILURE,
        ],
        state,
        action
      );
  }
};

const skincareIngredients = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [
          SKINCARE_CONSULTATION_INGREDIENTS_REQUEST,
          SKINCARE_CONSULTATION_INGREDIENTS_SUCCESS,
          SKINCARE_CONSULTATION_INGREDIENTS_FAILURE,
        ],
        state,
        action
      );
  }
};

const skincareIncis = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [
          SKINCARE_CONSULTATION_INCIS_REQUEST,
          SKINCARE_CONSULTATION_INCIS_SUCCESS,
          SKINCARE_CONSULTATION_INCIS_FAILURE,
        ],
        state,
        action
      );
  }
};

const haircareIncis = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [
          HAIRCARE_CONSULTATION_INCIS_REQUEST,
          HAIRCARE_CONSULTATION_INCIS_SUCCESS,
          HAIRCARE_CONSULTATION_INCIS_FAILURE,
        ],
        state,
        action
      );
  }
};

const scoring = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [CONSULTATION_SCORING_REQUEST, CONSULTATION_SCORING_SUCCESS, CONSULTATION_SCORING_FAILURE],
        state,
        action
      );
  }
};

const skincareScoring = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [
          SKINCARE_CONSULTATION_SCORING_REQUEST,
          SKINCARE_CONSULTATION_SCORING_SUCCESS,
          SKINCARE_CONSULTATION_SCORING_FAILURE,
        ],
        state,
        action
      );
  }
};

const productsVariants = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [
          GET_HAIR_PROFILE_PRODUCT_VARIANT_REQUEST,
          GET_HAIR_PROFILE_PRODUCT_VARIANT_SUCCESS,
          GET_HAIR_PROFILE_PRODUCT_VARIANT_FAILURE,
        ],
        state,
        action
      );
  }
};

const recommendations = (state = initialApiState, action) => {
  switch (action.type) {
    case userSignout.type:
    // fallthrough
    case INITIALIZE_CONSULTATION_REQUEST:
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
      return initialApiState;
    default:
      return apiReducer(
        [RECOMMENDATION_REQUEST, RECOMMENDATION_SUCCESS, RECOMMENDATION_FAILURE],
        state,
        action
      );
  }
};

const origin = (state = { startedFrom: null }, action) => {
  switch (action.type) {
    case userSignout.type:
      return { startedFrom: null };
    case INITIALIZE_CONSULTATION_REQUEST:
      return { ...state, startedFrom: action.origin };
    case GET_HAIR_PROFILE_REQUEST:
    case SAVE_ANSWER_SUCCESS:
    default:
      return state;
  }
};

export default combineReducers({
  aggressors,
  answers,
  bufferedAnswers,
  fragrances,
  fragrancesFeedback,
  ingredients,
  productsVariants,
  scoring,
  haircareIncis,
  skincareScoring,
  skincareFragrances,
  skincareIncis,
  skincareIngredients,
  skincareTextures,
  recommendations,
  origin,
});
